/**
 * Domain hook for Crew-embedded Certificates - Anchor Mode v2.0 compliant
 * Operates through crew storage only (legacy pattern)
 * Base key: 'crew' (vessel scoping handled by useStorageState)
 * RBAC-gated mutations
 * No vesselId in records
 * One write per save
 *
 * @example
 * const { crew, addCertificate, updateCertificate, archiveCertificate } = useCrewCertificates();
 *
 * // For merged view (crew + training certificates):
 * const certificates = useMergedCertificates(crewId);
 */

import { devLog } from '../../../utils/devLog';
import React, { useMemo, useCallback } from 'react';
import { useStorageState } from '../../useStorageState';
import { usePermissions } from '../../usePermissions';
import { useTrainingData } from '../useTrainingData';

// RBAC multi-signature shim
const canDo = (can, resource, action) => {
  try {
    if (!can || typeof can !== 'function') return false;
    return (
      (can.length >= 2 && can(resource, action)) ||
      can(`${resource}:${action}`) ||
      can({ resource, action }) ||
      can(action, resource)
    );
  } catch { 
    return false; 
  }
};

// Utility functions
const S = x => (x ?? '').toString().trim();
const toEpoch = v => (typeof v === 'number' ? v : (Number.isFinite(Date.parse(v)) ? Date.parse(v) : null));

// ID generator
const makeId = () => `cert_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

// FT-175: Normalize crew data ensuring certificates array exists
// Also filters out tombstoned records (deletedAt set) for consistency
const normalizeCrew = raw => Array.isArray(raw)
  ? raw
      .filter(m => !m.deletedAt)  // FT-175: Exclude tombstoned crew
      .map(m => ({
        ...m,
        certificates: Array.isArray(m.certificates) ? m.certificates : []
      }))
  : [];

export const useCrewCertificates = () => {
  // Version marker for dev tracking
  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      devLog('[useCrewCertificates] v2025-09-01 - Anchor Mode v2.0 compliant (embedded)');
    }
  }, []);

  // Base key only - vessel scoping handled by useStorageState  
  const { value: raw = [], setValue } = useStorageState('crew', []);
  const { can } = usePermissions();
  
  // Normalize on read
  const crew = useMemo(() => normalizeCrew(raw), [raw]);

  // Add certificate to crew member - RBAC-gated
  const addCertificate = useCallback((crewId, input = {}) => {
    if (!canDo(can, 'certificates', 'create')) {

      return null;
    }
    
    const now = Date.now();
    const cert = {
      id: input.id || makeId(),
      type: S(input.type || input.title),
      number: S(input.number),
      issuing_authority: S(input.issuing_authority || input.issuer),
      issue_date: toEpoch(input.issue_date || input.issueDate || input.issuedAt) ?? now,
      expiry_date: toEpoch(input.expiry_date || input.expiryDate || input.expiryAt) ?? null,
      status: (input.status || '').toString().toLowerCase() === 'archived' ? 
        'archived' : 
        ((toEpoch(input.expiry_date || input.expiryDate || input.expiryAt) ?? Infinity) < now ? 'expired' : 'active'),
      notes: S(input.notes),
      attachments: Array.isArray(input.attachments) ? input.attachments : [],
      created_at: new Date(now).toISOString(),
      updated_at: new Date(now).toISOString()
    };
    
    // ONE write per save
    setValue(prev => {
      const list = normalizeCrew(prev);
      return list.map(m => m.id === crewId
        ? { ...m, certificates: [...m.certificates, cert] }
        : m
      );
    });

    return cert.id;
  }, [can, setValue]);

  // Update certificate - RBAC-gated
  const updateCertificate = useCallback((crewId, certId, patch = {}) => {
    if (!canDo(can, 'certificates', 'update')) {

      return false;
    }
    
    const now = Date.now();
    
    // ONE write per save
    setValue(prev => {
      const list = normalizeCrew(prev);
      return list.map(m => {
        if (m.id !== crewId) return m;

        const certs = m.certificates.map(c => {
          if (c.id !== certId) return c;

          const issue_date = patch.issue_date !== undefined ?
            toEpoch(patch.issue_date || patch.issueDate || patch.issuedAt) :
            (c.issue_date ? toEpoch(c.issue_date) : c.issue_date);

          const expiry_date = patch.expiry_date !== undefined ?
            toEpoch(patch.expiry_date || patch.expiryDate || patch.expiryAt) :
            (c.expiry_date ? toEpoch(c.expiry_date) : c.expiry_date);

          const archived = (patch.status ?? c.status) === 'archived';
          const status = archived ? 'archived' : ((expiry_date ?? Infinity) < Date.now() ? 'expired' : 'active');

          return {
            ...c,
            ...(patch.type != null ? { type: S(patch.type || patch.title) } : {}),
            ...(patch.number != null ? { number: S(patch.number) } : {}),
            ...(patch.issuing_authority != null ? { issuing_authority: S(patch.issuing_authority || patch.issuer) } : {}),
            ...(patch.issue_date !== undefined ? { issue_date } : {}),
            ...(patch.expiry_date !== undefined ? { expiry_date } : {}),
            ...(Array.isArray(patch.attachments) ? { attachments: patch.attachments } : {}),
            ...(patch.notes != null ? { notes: S(patch.notes) } : {}),
            status,
            updated_at: new Date(now).toISOString()
          };
        });

        return { ...m, certificates: certs };
      });
    });

    return true;
  }, [can, setValue]);

  // Archive certificate - RBAC-gated
  const archiveCertificate = useCallback((crewId, certId) => {
    if (!canDo(can, 'certificates', 'archive')) {

      return false;
    }
    
    const now = Date.now();
    
    // ONE write per save
    setValue(prev => {
      const list = normalizeCrew(prev);
      return list.map(m => m.id !== crewId ? m : ({
        ...m,
        certificates: m.certificates.map(c => c.id === certId
          ? { ...c, status: 'archived', updated_at: new Date(now).toISOString() }
          : c
        )
      }));
    });

    return true;
  }, [can, setValue]);

  // Delete certificate - RBAC-gated
  const removeCertificate = useCallback((crewId, certId) => {
    if (!canDo(can, 'certificates', 'delete')) {

      return false;
    }
    
    // ONE write per save
    setValue(prev => {
      const list = normalizeCrew(prev);
      return list.map(m => m.id !== crewId ? m : ({
        ...m,
        certificates: m.certificates.filter(c => c.id !== certId)
      }));
    });

    return true;
  }, [can, setValue]);

  // Get all certificates across all crew
  const getAllCertificates = useCallback(() => {
    const allCerts = [];
    crew.forEach(member => {
      member.certificates.forEach(cert => {
        allCerts.push({
          ...cert,
          crewMemberId: member.id,
          crewMemberName: member.name || member.full_name // FT-189: canonical order
        });
      });
    });
    return allCerts;
  }, [crew]);

  // Get certificates for specific crew member
  const getByCrewId = useCallback((crewId) => {
    const member = crew.find(m => m.id === crewId);
    return member ? member.certificates : [];
  }, [crew]);

  // Get expiring certificates (within days threshold)
  const getExpiring = useCallback((daysThreshold = 30) => {
    const now = Date.now();
    const threshold = now + (daysThreshold * 24 * 60 * 60 * 1000);
    const expiring = [];
    
    crew.forEach(member => {
      member.certificates.forEach(cert => {
        const expiryMs = toEpoch(cert.expiry_date);
        if (cert.status === 'active' && expiryMs && expiryMs > now && expiryMs <= threshold) {
          expiring.push({
            ...cert,
            crewMemberId: member.id,
            crewMemberName: member.name || member.full_name // FT-189: canonical order
          });
        }
      });
    });
    
    return expiring;
  }, [crew]);

  // Get expired certificates
  const getExpired = useCallback(() => {
    const now = Date.now();
    const expired = [];
    
    crew.forEach(member => {
      member.certificates.forEach(cert => {
        const expiryMs = toEpoch(cert.expiry_date);
        if (cert.status === 'active' && expiryMs && expiryMs < now) {
          expired.push({
            ...cert,
            crewMemberId: member.id,
            crewMemberName: member.name || member.full_name // FT-189: canonical order
          });
        }
      });
    });
    
    return expired;
  }, [crew]);

  return {
    // Crew data with certificates
    crew,
    
    // CRUD operations (RBAC-gated)
    addCertificate,
    updateCertificate,
    archiveCertificate,
    removeCertificate,
    
    // Query helpers
    getAllCertificates,
    getByCrewId,
    getExpiring,
    getExpired,
    
    // Computed values
    totalCertificates: getAllCertificates().length,
    activeCertificates: getAllCertificates().filter(c => c.status === 'active').length,
    expiringCount: getExpiring(30).length,
    expiredCount: getExpired().length
  };
};

export default useCrewCertificates;

/**
 * Hook for getting merged certificates (crew + training-issued) for a specific crew member
 * Moves domain logic from CrewProfileView.js to domain layer (fixes Domain Spine violation)
 *
 * @param {string} crewId - Crew member ID
 * @returns {Array} Merged and sorted certificate array
 */
export function useMergedCertificates(crewId) {
  // Get crew data
  // FT-148 FIX: Changed from 'crew' to 'fleet_crew' - data is stored in fleet_crew key
  // FT-148b FIX: Must use noScope: true because fleet_crew is org-scoped, not vessel-scoped
  const { value: rawCrew = [] } = useStorageState('fleet_crew', [], { noScope: true });
  const crew = useMemo(() => normalizeCrew(rawCrew), [rawCrew]);

  // Get training data
  const { getTrainingByCrewId } = useTrainingData();

  // Merge certificates from crew object and training-issued certificates
  const mergedCertificates = useMemo(() => {
    if (!crewId) return [];

    // Find the crew member
    const crewMember = crew.find(m => m.id === crewId);
    if (!crewMember) return [];

    // Get crew's primary certificates (filter archived)
    const crewCerts = (crewMember.certificates || [])
      .filter(cert => cert.status !== 'archived')
      .map(cert => ({
        ...cert,
        source: 'crew',
        isFromTraining: false
      }));

    // Get training-issued certificates
    const crewTraining = getTrainingByCrewId(crewId);
    const trainingCerts = crewTraining
      .filter(session => session.certificateIssued && session.certificateDetails)
      .filter(session => session.trainees?.some(t => t.crewId === crewId))
      .map(session => {
        const cert = session.certificateDetails;
        const trainingDate = session.date || new Date().toISOString().split('T')[0];

        return {
          id: `TRAIN-${session.id}-${cert.certId || cert.name}`,
          type: cert.name || cert.type || 'Training Certificate',
          title: cert.name || cert.type || 'Training Certificate',
          number: cert.number || `TRAIN-${session.id}`,
          issueDate: trainingDate,
          issue_date: new Date(trainingDate).getTime(),
          expiryDate: cert.expiry || null,
          expiry_date: cert.expiry ? new Date(cert.expiry).getTime() : null,
          issued_by: 'Training Session',
          status: 'active',
          source: 'training',
          isFromTraining: true,
          trainingId: session.id,
          trainingRecordId: session.id,
          certificateDetails: cert
        };
      });

    // Combine arrays
    const allCerts = [...crewCerts, ...trainingCerts];

    // Deduplicate by certificate ID
    const uniqueCerts = allCerts.filter((cert, index, self) =>
      index === self.findIndex(c => c.id === cert.id)
    );

    // Mark archived certificates from crew.archivedCertificates
    const archivedIds = (crewMember.archivedCertificates || []).map(a => a.certificateId);
    const certsWithStatus = uniqueCerts.map(cert => {
      const archived = (crewMember.archivedCertificates || []).find(a => a.certificateId === cert.id);
      if (archived) {
        return {
          ...cert,
          status: 'archived',
          archivedAt: archived.archivedAt,
          archivedBy: archived.archivedBy,
          reason: archived.reason,
          notes: archived.notes
        };
      }
      return cert;
    });

    // Filter out archived certificates (they're visible in edit form only)
    const activeCerts = certsWithStatus.filter(cert => cert.status !== 'archived');

    // Sort by expiry date (soonest first, nulls last)
    activeCerts.sort((a, b) => {
      const dateA = a.expiry_date || a.expiryDate;
      const dateB = b.expiry_date || b.expiryDate;

      // No expiry dates go to end
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;

      // Compare dates (soonest first)
      const msA = typeof dateA === 'number' ? dateA : new Date(dateA).getTime();
      const msB = typeof dateB === 'number' ? dateB : new Date(dateB).getTime();
      return msA - msB;
    });

    return activeCerts;
  }, [crewId, crew, getTrainingByCrewId]);

  return mergedCertificates;
}